import { getCurrentUsageAndBalance } from '@/lib/api/currentUserApi'
import { GetCurrentUsageAndBalanceQuery, UserStatus } from '@/typings/domain'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { updateSubAccountThunk, destroySubAccountThunk } from './subAccountSlice'

type CurrentMonthlyUsage = NonNullable<
  GetCurrentUsageAndBalanceQuery['currentUser']
>['billingAccount']['currentMonthlyUsage']

type InitialUsageState = typeof initUsageState
type InitialCurrentMonthlyUsage = Pick<CurrentMonthlyUsage, 'resetDate'> & {
  entryUsage: InitialUsageState
  storageUsage: InitialUsageState
  smsUsage: InitialUsageState
  activeMailUsage: InitialUsageState
  entryTransactionUsage: InitialUsageState
  aiPointsUsage: InitialUsageState
  audioQuotaUsage: InitialUsageState
}

type UsageAndBalanceState = {
  occupiedUsersCount: number
  usersQuota: number
  realtimeTotalBalance: number
  debitBalance: NonNullable<GetCurrentUsageAndBalanceQuery['currentUser']>['billingAccount']['debitBalance']
} & (
  | { loaded: true; currentMonthlyUsage: CurrentMonthlyUsage }
  | { loaded: false; currentMonthlyUsage: InitialCurrentMonthlyUsage }
)

export type UsageAndBalance = Omit<Extract<UsageAndBalanceState, { loaded: true }>, 'loaded'>

const initUsageState = {
  balanceUsage: 0,
  boughtBalance: undefined,
  consumedQuota: undefined,
  currentMonthBalance: undefined,
  totalBalance: undefined,
  totalQuota: undefined,
}

const initialState: Extract<UsageAndBalanceState, { loaded: false }> = {
  loaded: false,
  occupiedUsersCount: 0,
  usersQuota: 0,
  realtimeTotalBalance: 0,
  currentMonthlyUsage: {
    resetDate: '',
    entryUsage: { ...initUsageState },
    storageUsage: { ...initUsageState },
    smsUsage: { ...initUsageState },
    activeMailUsage: { ...initUsageState },
    entryTransactionUsage: { ...initUsageState },
    aiPointsUsage: { ...initUsageState },
    audioQuotaUsage: { ...initUsageState },
  },
  debitBalance: {
    balance: 0,
    smsBalance: 0,
    emailBalance: 0,
    aiPointsBalance: 0,
    audioBalance: 0,
  },
}

export const usageAndBalanceSlice = createSlice({
  name: 'usageAndBalance',
  initialState: initialState as UsageAndBalanceState,
  reducers: {
    adjustOccupiedUsersCount: (state, action: { payload: number }) => {
      state.occupiedUsersCount = Math.max(0, state.occupiedUsersCount + action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getUsageAndBalanceThunk.fulfilled, (state, action) => {
        state.occupiedUsersCount = action.payload.occupiedUsersCount
        state.usersQuota = action.payload.usersQuota
        state.realtimeTotalBalance = action.payload.realtimeTotalBalance
        state.currentMonthlyUsage = action.payload.currentMonthlyUsage
        state.debitBalance = action.payload.debitBalance
        state.loaded = true
      })
      .addCase(updateSubAccountThunk.fulfilled, (state, action) => {
        // 当子账号状态变化时更新占用用户数
        // 只有在payload中包含status字段时才调整（表示状态变化）
        if (action.payload.status === UserStatus.Active) {
          // 状态变为Active，增加计数
          state.occupiedUsersCount += 1
        } else if (action.payload.status === UserStatus.Disabled) {
          // 状态变为Disabled，减少计数
          state.occupiedUsersCount = Math.max(0, state.occupiedUsersCount - 1)
        }
      })
      .addCase(destroySubAccountThunk.fulfilled, (state) => {
        // 删除子账号时减少计数
        state.occupiedUsersCount = Math.max(0, state.occupiedUsersCount - 1)
      })
  },
})

export const { adjustOccupiedUsersCount } = usageAndBalanceSlice.actions

export const getUsageAndBalanceThunk = createAsyncThunk<UsageAndBalance, void>(
  'usageAndBalance/getCurrentUsageAndBalance',
  async (_payload, thunkAPI) => {
    const { data } = await getCurrentUsageAndBalance()
    return data.currentUser?.billingAccount ?? thunkAPI.rejectWithValue('failed to get results')
  }
)

export default usageAndBalanceSlice.reducer
